import React, { useMemo, useState } from "react";
import { Filter, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";

/**
 * AlertFilterPanel — client-side filter for the admin vetting queue.
 *
 * The pending alert doc already carries every signal we need
 * (`conviction_grade`, `confidence`, `conviction_stack.{insider,catalyst,squeeze}`,
 * `screen.signals`, `news_sentiment.score`, `sector`), so we filter in the
 * browser — instant, no backend round-trips, no schema changes.
 *
 * Filters mirror the "big-move driver" callouts from the desk:
 *   • Conviction grade (A → F) + min confidence
 *   • Earnings catalyst in the next N days
 *   • Insider buying activity — distinct filers, executive, $-value floor
 *   • News sentiment score floor
 *   • Short-squeeze setup (SI% ≥ 15% AND DTC ≥ 3)
 *   • Screen signals fired (Coiled Spring / Stealth Loading / Relative Lift)
 *   • Alert class + sector
 *
 * Renders as a collapsible panel above the pending list. When collapsed,
 * shows the count-through so the admin sees at a glance how the queue
 * narrows as they tune filters.
 */
export default function AlertFilterPanel({ alerts, onFilteredChange }) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    minGrade: "any",           // any | A | B | C | D | F
    minConfidence: 0,          // 0.0 – 1.0
    alertClass: "any",         // any | steady | moonshot
    earningsWithinDays: 0,     // 0 = disabled
    insiderBuying: false,      // ≥ 1 distinct filer
    insiderExecutive: false,   // has_executive === true
    insiderMinUsd: 0,          // 0 = disabled
    minNewsScore: 0,           // 0 = disabled
    squeezeSetup: false,       // SI% ≥ 15 AND DTC ≥ 3
    signals: {},               // { "Coiled Spring": true, ... } — AND semantics
    sector: "any",
  });

  const sectorOptions = useMemo(() => {
    const set = new Set((alerts || []).map((a) => a.sector).filter(Boolean));
    return ["any", ...Array.from(set).sort()];
  }, [alerts]);

  const signalOptions = useMemo(() => {
    const set = new Set();
    (alerts || []).forEach((a) => {
      (a.screen?.signals || []).forEach((s) => s?.name && set.add(s.name));
    });
    return Array.from(set).sort();
  }, [alerts]);

  const gradeRank = { A: 5, B: 4, C: 3, D: 2, F: 1 };

  const filtered = useMemo(() => {
    return (alerts || []).filter((a) => {
      if (filters.minGrade !== "any") {
        const need = gradeRank[filters.minGrade] || 0;
        const have = gradeRank[(a.conviction_grade || "F").toUpperCase()] || 0;
        if (have < need) return false;
      }
      if (filters.minConfidence > 0 && (a.confidence ?? 0) < filters.minConfidence) return false;
      if (filters.alertClass !== "any" && (a.alert_class || "steady") !== filters.alertClass) return false;

      const cat = a.conviction_stack?.catalyst;
      if (filters.earningsWithinDays > 0) {
        const days = cat?.next_earnings_days_away;
        if (days == null || days < 0 || days > filters.earningsWithinDays) return false;
      }

      const insider = a.conviction_stack?.insider;
      if (filters.insiderBuying && !((insider?.distinct_filers ?? 0) >= 1)) return false;
      if (filters.insiderExecutive && !insider?.has_executive) return false;
      if (filters.insiderMinUsd > 0 && (insider?.total_value_usd ?? 0) < filters.insiderMinUsd) return false;

      if (filters.minNewsScore > 0 && (a.news_sentiment?.score ?? 0) < filters.minNewsScore) return false;

      if (filters.squeezeSetup) {
        const sq = a.conviction_stack?.squeeze;
        const si = sq?.short_interest_pct ?? 0;
        const dtc = sq?.days_to_cover ?? 0;
        if (!(si >= 15 && dtc >= 3)) return false;
      }

      const wantedSignals = Object.keys(filters.signals).filter((s) => filters.signals[s]);
      if (wantedSignals.length > 0) {
        const firedNames = new Set(
          (a.screen?.signals || []).filter((s) => s?.fired).map((s) => s.name),
        );
        for (const w of wantedSignals) if (!firedNames.has(w)) return false;
      }

      if (filters.sector !== "any" && a.sector !== filters.sector) return false;

      return true;
    });
  }, [alerts, filters]);

  // Hoist the filtered list up. Runs on every mount/filter change without
  // causing an infinite loop because `filtered` is memoised on stable deps.
  React.useEffect(() => { onFilteredChange && onFilteredChange(filtered); }, [filtered, onFilteredChange]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (filters.minGrade !== "any") n++;
    if (filters.minConfidence > 0) n++;
    if (filters.alertClass !== "any") n++;
    if (filters.earningsWithinDays > 0) n++;
    if (filters.insiderBuying) n++;
    if (filters.insiderExecutive) n++;
    if (filters.insiderMinUsd > 0) n++;
    if (filters.minNewsScore > 0) n++;
    if (filters.squeezeSetup) n++;
    n += Object.values(filters.signals).filter(Boolean).length;
    if (filters.sector !== "any") n++;
    return n;
  }, [filters]);

  const reset = () => setFilters({
    minGrade: "any", minConfidence: 0, alertClass: "any", earningsWithinDays: 0,
    insiderBuying: false, insiderExecutive: false, insiderMinUsd: 0,
    minNewsScore: 0, squeezeSetup: false, signals: {}, sector: "any",
  });

  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const updSig = (name, v) => setFilters((f) => ({ ...f, signals: { ...f.signals, [name]: v } }));

  return (
    <div className="bg-white border border-mb-border rounded-lg mb-4 shadow-sm" data-testid="alert-filter-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-neutral-50"
        data-testid="alert-filter-toggle"
      >
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-mb-emerald" />
          <span className="font-display font-semibold text-mb-ink">Filter the queue</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-mb-emerald text-white text-[10px] font-mono-data" data-testid="alert-filter-active-count">
              {activeCount} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-neutral-700">
          <span className="font-mono-data font-semibold" data-testid="alert-filter-matching">
            {filtered.length} / {alerts?.length || 0} matching
          </span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="alert-filter-body">
          {/* Conviction + confidence */}
          <div className="space-y-2">
            <div className="label-uppercase text-neutral-800 font-bold">Conviction</div>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-32 text-neutral-700 font-medium">Min grade</label>
              <select className="border border-mb-border rounded px-2 py-1 text-sm flex-1 text-mb-ink bg-white" value={filters.minGrade} onChange={(e) => upd("minGrade", e.target.value)} data-testid="filter-min-grade">
                <option value="any">Any</option><option value="A">A only</option><option value="B">B+</option><option value="C">C+</option><option value="D">D+</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-32 text-neutral-700 font-medium">Min confidence</label>
              <input type="range" min="0" max="1" step="0.05" value={filters.minConfidence} onChange={(e) => upd("minConfidence", parseFloat(e.target.value))} className="flex-1" data-testid="filter-min-confidence" />
              <span className="font-mono-data text-mb-emerald-dark font-bold w-10 text-right">{filters.minConfidence.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-32 text-neutral-700 font-medium">Class</label>
              <select className="border border-mb-border rounded px-2 py-1 text-sm flex-1 text-mb-ink bg-white" value={filters.alertClass} onChange={(e) => upd("alertClass", e.target.value)} data-testid="filter-alert-class">
                <option value="any">Any</option><option value="steady">Steady only</option><option value="moonshot">Moonshot only</option>
              </select>
            </div>
          </div>

          {/* Catalyst */}
          <div className="space-y-2">
            <div className="label-uppercase text-neutral-800 font-bold">Catalyst</div>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-32 text-neutral-700 font-medium">Earnings within</label>
              <input type="number" min="0" max="90" value={filters.earningsWithinDays} onChange={(e) => upd("earningsWithinDays", parseInt(e.target.value || "0", 10))} className="border border-mb-border rounded px-2 py-1 text-sm w-20 font-mono-data text-mb-ink" data-testid="filter-earnings-days" />
              <span className="text-neutral-600 text-xs">days (0 = off)</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-32 text-neutral-700 font-medium">News score ≥</label>
              <input type="range" min="0" max="100" step="5" value={filters.minNewsScore} onChange={(e) => upd("minNewsScore", parseInt(e.target.value, 10))} className="flex-1" data-testid="filter-news-score" />
              <span className="font-mono-data text-mb-emerald-dark font-bold w-10 text-right">{filters.minNewsScore}</span>
            </div>
          </div>

          {/* Insider */}
          <div className="space-y-2">
            <div className="label-uppercase text-neutral-800 font-bold">Insider activity</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer text-neutral-800">
              <input type="checkbox" checked={filters.insiderBuying} onChange={(e) => upd("insiderBuying", e.target.checked)} data-testid="filter-insider-buying" />
              <span>At least 1 insider buy (90d)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer text-neutral-800">
              <input type="checkbox" checked={filters.insiderExecutive} onChange={(e) => upd("insiderExecutive", e.target.checked)} data-testid="filter-insider-exec" />
              <span>Includes an executive filer</span>
            </label>
            <div className="flex items-center gap-2 text-sm">
              <label className="w-32 text-neutral-700 font-medium">Min $ bought</label>
              <input type="number" min="0" step="10000" value={filters.insiderMinUsd} onChange={(e) => upd("insiderMinUsd", parseInt(e.target.value || "0", 10))} className="border border-mb-border rounded px-2 py-1 text-sm w-28 font-mono-data text-mb-ink" data-testid="filter-insider-usd" />
            </div>
          </div>

          {/* Screen signals */}
          <div className="space-y-2">
            <div className="label-uppercase text-neutral-800 font-bold">Screen signals fired</div>
            {signalOptions.length === 0 && <div className="text-xs text-neutral-600">No signals in queue yet.</div>}
            {signalOptions.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm cursor-pointer text-neutral-800">
                <input type="checkbox" checked={!!filters.signals[s]} onChange={(e) => updSig(s, e.target.checked)} data-testid={`filter-signal-${s.replace(/\s+/g, "-").toLowerCase()}`} />
                <span>{s}</span>
              </label>
            ))}
          </div>

          {/* Squeeze */}
          <div className="space-y-2">
            <div className="label-uppercase text-neutral-800 font-bold">Squeeze setup</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer text-neutral-800">
              <input type="checkbox" checked={filters.squeezeSetup} onChange={(e) => upd("squeezeSetup", e.target.checked)} data-testid="filter-squeeze" />
              <span>Short interest ≥ 15% <em>and</em> DTC ≥ 3</span>
            </label>
            <div className="text-[11px] text-neutral-600 italic leading-snug">
              High-SI, high-DTC setups have historically produced our biggest moonshot runs when combined with a catalyst.
            </div>
          </div>

          {/* Sector */}
          <div className="space-y-2">
            <div className="label-uppercase text-neutral-800 font-bold">Sector</div>
            <select className="border border-mb-border rounded px-2 py-1 text-sm w-full text-mb-ink bg-white" value={filters.sector} onChange={(e) => upd("sector", e.target.value)} data-testid="filter-sector">
              {sectorOptions.map((s) => <option key={s} value={s}>{s === "any" ? "Any sector" : s}</option>)}
            </select>
          </div>

          {/* Actions */}
          <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between pt-3 border-t border-mb-border">
            <div className="text-xs text-neutral-600">
              Filters run in the browser on the loaded queue — instant, no backend hit.
            </div>
            <Button variant="outline" size="sm" onClick={reset} disabled={activeCount === 0} className="rounded-md" data-testid="filter-reset">
              <X className="w-3.5 h-3.5 mr-1" /> Clear all
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
